import OfferFormAction from './offer-form-action';
import GeneralUtility from '../../../utilities/general-utility';
import moment from 'moment';
import SubpagesFormAction from './subpages-form-action';
import StudentsLinkAction from './students-link-action';
import StudentsListAction from './students-list-action';

/**
 * Initializes all actions for checklist steps.
 *
 * @author Reinhard Führicht <rf@typoheads.at>
 */
export default class ChecklistStep {
    constructor(containers: NodeListOf<HTMLElement>) {
        containers.forEach(container => {
            let actionContainer = container.querySelector<HTMLElement>('[data-action="offerForm"]');
            if (actionContainer) {
                new OfferFormAction(actionContainer);
                return;
            }

            actionContainer = container.querySelector<HTMLElement>('[data-action="subpagesForm"]');
            if (actionContainer) {
                new SubpagesFormAction(actionContainer);
                return;
            }

            actionContainer = container.querySelector<HTMLElement>('[data-action="studentsLink"]');
            if (actionContainer) {
                new StudentsLinkAction(actionContainer);
                return;
            }

            actionContainer = container.querySelector<HTMLElement>('[data-action="studentsList"]');
            if (actionContainer) {
                new StudentsListAction(actionContainer);
                return;
            }
        });

        this.initializeNotesForm();
        this.initializeInsertArrivalDepartureDateButton();
    }

    /**
     * Initializes AJAX saving of notes form.
     */
    protected initializeNotesForm(): void {
        const notesForm = document.querySelector<HTMLElement>('#notes-form-step');
        if (!notesForm) {
            return;
        }
        const saveNotes = notesForm.querySelector<HTMLElement>('.save-notes');
        if (!saveNotes) {
            return;
        }

        saveNotes.addEventListener('click', event => {
            const stepWrap = document.querySelector<HTMLElement>('#checkliststep-content');
            const stepContainer = document.querySelector<HTMLElement>('#checkliststep-container');
            if (!stepWrap || !stepContainer || !stepContainer.dataset.checklist || !stepContainer.dataset.stepType) {
                return;
            }
            const checklist = parseInt(stepContainer.dataset.checklist);
            const stepType = stepContainer.dataset.stepType;
            const notesElement = notesForm.querySelector('textarea');
            if (!notesElement) {
                return;
            }
            let checklistStep = 0;
            if (stepContainer.dataset.checklistStep) {
                checklistStep = parseInt(stepContainer.dataset.checklistStep);
            }

            fetch(GeneralUtility.getUrl() + '?offer-api[endpoint][]=checklist&offer-api[endpoint][]=saveNotes&pageUid=' + GeneralUtility.pageUid + '&checklist=' + checklist + '&checklistStep=' + checklistStep + '&stepType=' + stepType + '&notes=' + notesElement.value + '&rand=' + Math.random())
                .then(response => {
                    if (response.status !== 200) {
                        throw new Error(response.status + ' ' + response.statusText);
                    }
                    return response.json();
                })
                .then(data => {
                    // If a note was added to a checklist step, that was not saved yet, the checklist step UID now needs to be updated. Otherwise, subsequent
                    // saving of the notes on the same page would lead to duplicated steps.
                    if (!stepContainer.dataset.checklistStep || stepContainer.dataset.checklistStep === '0') {
                        stepContainer.dataset.checklistStep = data.checklistStep;
                    }

                    const alertTemplate = notesForm.querySelector<HTMLElement>('.alert-template');
                    const alertPasted = notesForm.querySelector<HTMLElement>('.alert-pasted');
                    const buttonNotesEdit = stepWrap.querySelector<HTMLElement>('.button-notes-edit');
                    const buttonNotesAdd = stepWrap.querySelector<HTMLElement>('.button-notes-add');
                    if (!alertTemplate || !buttonNotesEdit || !buttonNotesAdd) {
                        return;
                    }

                    // Switch the create/edit-notes-buttons
                    if (!alertPasted) {
                        notesForm.insertAdjacentHTML('afterbegin', alertTemplate.innerHTML);
                        // Select alert-success after we have re-injected it into the DOM
                        notesForm.querySelector<HTMLElement>('.alert-success')?.classList.add('show', 'alert-pasted');
                    } else {
                        notesForm.querySelector<HTMLElement>('.alert-success')?.classList.add('show');
                    }

                    // Notes were cleared; show "create new" buttons
                    if (!data.notes) {
                        buttonNotesEdit.classList.add('d-none');
                        buttonNotesAdd.classList.remove('d-none');
                    }
                    // Notes were updated with something non-empty; show "edit" buttons
                    else {
                        buttonNotesEdit.classList.remove('d-none');
                        buttonNotesAdd.classList.add('d-none');
                    }
                });
            event.preventDefault();
            return false;
        });
    }

    /**
     * Initializes the button to insert the arrival/departure date from the checklist settings into the filter form.
     */
    protected initializeInsertArrivalDepartureDateButton(): void {
        const insertDatesButton = document.querySelector<HTMLElement>('#insert-arrival-departure-date');
        const validFromField = document.querySelector<HTMLInputElement>('#validFrom-field');
        const validFromHiddenField = document.querySelector<HTMLInputElement>('#validFrom');
        const validToField = document.querySelector<HTMLInputElement>('#validTo-field');
        const validToHiddenField = document.querySelector<HTMLInputElement>('#validTo');
        if (!insertDatesButton || !validFromField || !validFromHiddenField || !validToField || !validToHiddenField) {
            return;
        }

        insertDatesButton.addEventListener('click', () => {
            if (insertDatesButton.dataset.arrival) {
                const arrivalTimestamp = parseInt(insertDatesButton.dataset.arrival);
                const arrivalMoment = moment.unix(arrivalTimestamp);
                if (arrivalMoment.isValid()) {
                    validFromField.value = arrivalMoment.format('DD.MM.YYYY');
                    validFromHiddenField.value = arrivalTimestamp.toString();
                }
            }

            if (insertDatesButton.dataset.departure) {
                const departureTimestamp = parseInt(insertDatesButton.dataset.departure);
                const departureMoment = moment.unix(departureTimestamp);
                if (departureMoment.isValid()) {
                    validToField.value = departureMoment.format('DD.MM.YYYY');
                    validToHiddenField.value = departureTimestamp.toString();
                }
            }
        });
    }
}
